import type { PersonaDetails } from '@carboncopyco/types/desktop';
import {
  Avatar,
  Button,
  Checkbox,
  Form,
  InputTextField,
  Modal,
  TextBase,
} from '@carboncopyco/ui';
import { useAnalytics } from '@carboncopyco/utils';
import { useEffect, useRef, useState } from 'react';
import { object, string } from 'yup';
import { AnalyticsEvents } from '../../../shared/constants/analytics.events';
import { getInitials } from '../../../util/util';
import { assignColor } from '../../shared/utils';
import ModalWrapper from '../Modals/ModalWrapper';

interface PersonaAddNewModalProps {
  open: boolean;
  onClose: () => void;
  persona?: PersonaDetails & {
    id: string;
    isDefault: boolean;
  };
}

// eslint-disable-next-line import/no-unused-modules
export const PersonaAddNewModal = ({
  open,
  onClose,
  persona,
}: PersonaAddNewModalProps) => {
  const initialValues = {
    name: persona?.name || '',
    description: persona?.description || '',
  };
  const { trackEvent } = useAnalytics();
  const validationSchema = object({
    name: string().required('Required'),
  }).required();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [formValues, setFormValues] =
    useState<Record<string, string>>(initialValues);

  const [isDefaultChecked, setIsDefaultChecked] = useState<boolean>(
    persona?.isDefault || false
  );

  const handleAddNewPersona = () => {
    window.dsDesktop?.user.createNewPersona({
      persona: formValues,
      isDefault: isDefaultChecked,
    });
    trackEvent(AnalyticsEvents.CLICK_ADD_PERSONA, {
      location: 'desktop',
    });
    onClose();
  };

  const handleEditPersona = () => {
    window.dsDesktop?.user.editPersona({
      persona: formValues,
      personaId: persona?.id,
      isDefault: isDefaultChecked,
    });
    onClose();
  };

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  return (
    <ModalWrapper isVisible>
      <Modal
        title={persona?.name ? 'Edit profile' : 'Add new profile'}
        open={open}
        onClose={onClose}
      >
        <Form
          enableReinitialize
          onSubmit={
            persona?.name === undefined
              ? handleAddNewPersona
              : handleEditPersona
          }
          initialValues={initialValues}
          onValueChange={setFormValues}
          validationSchema={validationSchema}
        >
          <div className="flex flex-col w-ds-md-modal">
            <div className="mt-2 mb-3">
              <TextBase
                color="text-ds-natural-600"
                label="Add indicative name and description that can be helpful when selecting a profile during presentation."
              />
              <div className="flex pt-3">
                <div className="w-2/3">
                  <InputTextField
                    ref={nameInputRef}
                    required
                    label="Profile name"
                    name="name"
                    inputSize="lg"
                  />
                  <InputTextField
                    label="Profile description"
                    name="description"
                    inputSize="lg"
                  />
                </div>

                <div className="w-1/3 flex justify-center items-center">
                  <Avatar
                    initials={getInitials(formValues.name) || undefined}
                    size="xlarge"
                    backgroundColor={assignColor(formValues.name)}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col text-center">
              {!persona?.name && (
                <div className="flex flex-row pl-1 items-center leading-4">
                  <Checkbox
                    label={
                      <TextBase
                        color="text-ds-natural-600"
                        label="Set as default"
                      />
                    }
                    name="set-as-default"
                    onChange={(e) => {
                      setIsDefaultChecked(e.target.checked);
                    }}
                    checked={isDefaultChecked}
                  />
                </div>
              )}
              <div className="flex self-end  mt-8 gap-3">
                <Button
                  link
                  secondary
                  label="Cancel"
                  onClick={onClose}
                />
                <Button
                  submit
                  label="Add"
                />
              </div>
            </div>
          </div>
        </Form>
      </Modal>
    </ModalWrapper>
  );
};
